/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  TpConfigsInfo,
  TpSections,
  TP_ALL_SECTIONS,
  TP_SECTIONS,
} from "../models/tp-sections-info";

@Injectable({
  providedIn: "root",
})
export class TpService {
  private currentTpSections$ = new BehaviorSubject<TpSections>(TP_SECTIONS);

  /**
   * Retrieve the treatment plan sections
   *
   * @param configToRemove List of configuration cestions to remove from the list.
   * eg. ['base.therapies', 'advanced.app-features']
   *
   * @returns Desidered tp sections.
   */
  getTpSections(configToRemove: string[] = []): TpSections {
    const tpSections = this.cloneTpSections();

    configToRemove.forEach((configPath) => {
      const path = configPath.split(".");

      switch (path.length) {
        case 1:
          delete tpSections[path[0]];
          break;

        case 2:
          delete tpSections[path[0]].items[1];
          break;

        default:
          throw new Error(
            `Wrong tp section path. It must be on the form 'section-name' or 'section-name.config-name'`
          );
      }

      delete tpSections[path[0]].items[path[1]];
    });

    // Remove empty sections
    Object.keys(tpSections).forEach((sectionId) => {
      if (Object.keys(tpSections[sectionId].items).length === 0) {
        delete tpSections[sectionId];
      }
    });

    return tpSections;
  }

  /**
   * Get a custom tp sections list contaning only declared configurations
   *
   * @param selectedConfigIds A list of config ids to put in the returned list.
   * @returns Desidered tp sections.
   */
  getTpSectionsByIds(selectedConfigIds: string[]): TpSections {
    const tpSections = this.cloneTpSections();

    Object.entries(tpSections).forEach(([sectionId, section]) => {
      Object.keys(section.items).forEach((configId) => {
        if (!selectedConfigIds.includes(`${sectionId}.${configId}`)) {
          delete section.items[configId];
        }
      });

      // Remove section if empty
      if (Object.keys(section.items).length === 0) {
        delete tpSections[sectionId];
      }
    });

    return tpSections;
  }

  getAllTpSectionsConfigs(): TpConfigsInfo {
    return TP_ALL_SECTIONS;
  }

  registerCurrentTpSections(tpSections: TpSections) {
    this.currentTpSections$.next(tpSections);
  }

  getCurrentSubScection(sectionId: string): Observable<TpConfigsInfo | null> {
    return this.currentTpSections$.asObservable().pipe(
      map((sections) => {
        const section = sections[sectionId];
        return section ? section.items : null;
      })
    );
  }

  private cloneTpSections(): TpSections {
    return JSON.parse(JSON.stringify(TP_SECTIONS));
  }
}
